import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2, MailX } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const useTurnstile = (siteKey, onToken, onFail) => {
  const host = useRef(null);
  const widget = useRef(null);
  useEffect(() => {
    if (!siteKey || !host.current) return undefined;
    let timer;
    const render = () => {
      if (!window.turnstile || !host.current || widget.current) return;
      clearTimeout(timer);
      widget.current = window.turnstile.render(host.current, {
        sitekey: siteKey,
        action: "travel_list",
        theme: "light",
        callback: (t) => onToken(t),
        "expired-callback": () => onToken(""),
        "error-callback": () => { onToken(""); onFail(); },
      });
    };
    let s = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
    if (window.turnstile) render();
    else {
      if (!s) {
        s = document.createElement("script");
        s.src = TURNSTILE_SRC;
        s.async = true;
        document.head.appendChild(s);
      }
      s.addEventListener("load", render);
      s.addEventListener("error", onFail);
      timer = setTimeout(() => { if (!widget.current) onFail(); }, 10000);
    }
    return () => {
      clearTimeout(timer);
      s?.removeEventListener("load", render);
      s?.removeEventListener("error", onFail);
      if (widget.current) { window.turnstile?.remove(widget.current); widget.current = null; }
    };
  }, [siteKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const reset = () => widget.current && window.turnstile?.reset(widget.current);
  return { host, reset };
};

const interests = ["Resort Stay", "Cruise", "Group Travel", "Travel Deals", "Travel Essentials"];
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const LeadCapture = ({ presetInterest, presetOfferId, presetOfferTitle }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gate, setGate] = useState({ token: "", enabled: true, turnstile_site_key: "" });
  const [captcha, setCaptcha] = useState("");
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const turnstile = useTurnstile(gate.turnstile_site_key, setCaptcha, () => setCaptchaFailed(true));
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    phone: "",
    travel_interest: "",
    destination: "",
    travel_month: "",
    group_size: "",
    notes: "",
    consent: false,
    company: "", // honeypot
  });

  const interest = presetInterest && interests.includes(presetInterest)
    ? presetInterest
    : form.travel_interest;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchGate = () =>
    axios.get(`${API}/leads/token`).then(({ data }) => setGate(data)).catch(() => {});
  useEffect(() => { fetchGate(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent to join the Travel List.");
      return;
    }
    if (gate.turnstile_site_key && !captcha) {
      toast.error(captchaFailed
        ? "The security check couldn't load. Please email us at travel@kislivingvacations.com."
        : "Please complete the security check first.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        form_token: gate.token,
        turnstile_token: captcha || null,
        travel_interest: interest || form.travel_interest,
        offer_id: presetOfferId || null,
        offer_title: presetOfferTitle || null,
        source: presetOfferTitle ? "offer" : "homepage",
      });
      trackEvent("lead_submit", { interest: interest || form.travel_interest, location: "homepage", offer_id: presetOfferId || null });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) setGate((g) => ({ ...g, enabled: false }));
      if (status === 400) { fetchGate(); setCaptcha(""); turnstile.reset(); }
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="lead-capture" data-testid="lead-capture-section" className="py-20 md:py-32 bg-sage relative overflow-hidden">
      <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-10">
          <p className="uppercase text-sm tracking-[0.2em] font-semibold text-white/70 mb-4">
            Be the first to know
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            Join the KIS Living Travel List
          </h2>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-white/85 max-w-xl mx-auto">
            Be the first to receive curated vacation ideas, resort stay opportunities, cruise
            options, group travel updates, and simple travel tips.
          </p>
          {presetOfferTitle && (
            <div data-testid="lead-offer-note" className="mt-5 inline-block bg-white/15 border border-white/25 text-white text-sm rounded-full px-5 py-2">
              Requesting info about: <span className="font-semibold">{presetOfferTitle}</span>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl p-7 sm:p-10 shadow-xl">
          {!gate.enabled ? (
            <div data-testid="lead-form-paused" className="text-center py-10">
              <MailX className="mx-auto text-terracotta mb-5" size={56} strokeWidth={1.5} />
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-ink mb-3">
                Sign-ups are paused for a moment
              </h3>
              <p className="text-base leading-relaxed text-ink-soft max-w-md mx-auto">
                We'd still love to hear from you — email us directly at{" "}
                <a href="mailto:travel@kislivingvacations.com" data-testid="lead-paused-email" className="text-terracotta font-semibold underline">
                  travel@kislivingvacations.com
                </a>{" "}
                or call <a href="tel:18335475433" className="text-terracotta font-semibold">1.833.547.5433</a>.
              </p>
            </div>
          ) : submitted ? (
            <div data-testid="lead-success-message" className="text-center py-10">
              <CheckCircle2 className="mx-auto text-terracotta mb-5" size={56} strokeWidth={1.5} />
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-ink mb-3">
                You're on the list!
              </h3>
              <p className="text-base leading-relaxed text-ink-soft max-w-md mx-auto">
                Thank you for joining the KIS Living Travel List. We'll be in touch with curated
                travel ideas and updates.
              </p>
            </div>
          ) : (
            <form data-testid="lead-capture-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <Label htmlFor="first_name" className="text-ink font-medium">First Name</Label>
                <Input
                  id="first_name"
                  data-testid="lead-first-name"
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  placeholder="Your first name"
                  className="bg-white border-bone rounded-xl h-12 focus-visible:ring-terracotta"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-ink font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="lead-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@email.com"
                  className="bg-white border-bone rounded-xl h-12 focus-visible:ring-terracotta"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="phone" className="text-ink font-medium">Phone (optional)</Label>
                <Input
                  id="phone"
                  data-testid="lead-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+1 (000) 000-0000"
                  className="bg-white border-bone rounded-xl h-12 focus-visible:ring-terracotta"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-ink font-medium">Travel Interest</Label>
                <Select value={interest} onValueChange={(v) => update("travel_interest", v)}>
                  <SelectTrigger data-testid="lead-interest" className="bg-white border-bone rounded-xl h-12 focus:ring-terracotta">
                    <SelectValue placeholder="Select interest" />
                  </SelectTrigger>
                  <SelectContent>
                    {interests.map((i) => (
                      <SelectItem key={i} value={i} data-testid={`lead-interest-${i.toLowerCase().replace(/\s+/g, "-")}`}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="destination" className="text-ink font-medium">Preferred Destination</Label>
                <Input
                  id="destination"
                  data-testid="lead-destination"
                  value={form.destination}
                  onChange={(e) => update("destination", e.target.value)}
                  placeholder="e.g. Caribbean, Mexico"
                  className="bg-white border-bone rounded-xl h-12 focus-visible:ring-terracotta"
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label className="text-ink font-medium">Preferred Travel Month</Label>
                <Select value={form.travel_month} onValueChange={(v) => update("travel_month", v)}>
                  <SelectTrigger data-testid="lead-month" className="bg-white border-bone rounded-xl h-12 focus:ring-terracotta">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="group_size" className="text-ink font-medium">Group Size</Label>
                <Input
                  id="group_size"
                  data-testid="lead-group-size"
                  value={form.group_size}
                  onChange={(e) => update("group_size", e.target.value)}
                  placeholder="e.g. 2 adults, 4 people"
                  className="bg-white border-bone rounded-xl h-12 focus-visible:ring-terracotta"
                />
              </div>

              <div className="sm:col-span-2 flex flex-col gap-2">
                <Label htmlFor="lead-message" className="text-ink font-medium">Anything else? (optional)</Label>
                <textarea
                  id="lead-message"
                  data-testid="lead-message"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Tell us about your travel style, occasion, or questions"
                  rows={3}
                  className="bg-white border border-bone rounded-xl px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta"
                />
              </div>

              {/* Honeypot field (hidden from humans) */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.company}
                onChange={(e) => update("company", e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />

              <div className="sm:col-span-2 flex items-start gap-2">
                <input
                  id="lead-consent"
                  type="checkbox"
                  data-testid="lead-consent"
                  checked={form.consent}
                  onChange={(e) => update("consent", e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#C86A51]"
                />
                <label htmlFor="lead-consent" className="text-sm text-ink-soft leading-relaxed">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-terracotta underline hover:text-terracotta-hover">Terms of Service</a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-terracotta underline hover:text-terracotta-hover">Privacy Policy</a>, and to receive curated travel ideas and updates from KIS Living Vacations. You can unsubscribe anytime.
                </label>
              </div>

              <div className="sm:col-span-2 mt-2">
                {gate.turnstile_site_key && (
                  <div data-testid="lead-turnstile" ref={turnstile.host} className="mb-4 flex justify-center" />
                )}
                {gate.turnstile_site_key && captchaFailed && !captcha && (
                  <p data-testid="lead-turnstile-fallback" className="mb-4 text-center text-sm text-ink-soft">
                    The security check couldn't load (an ad blocker or network filter may be stopping it). Please email us at{" "}
                    <a href="mailto:travel@kislivingvacations.com" className="text-terracotta font-semibold underline">travel@kislivingvacations.com</a>{" "}
                    or call <a href="tel:18335475433" className="text-terracotta font-semibold">1.833.547.5433</a>.
                  </p>
                )}
                <Button
                  type="submit"
                  data-testid="lead-submit-button"
                  disabled={loading}
                  className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white py-6 text-base h-auto"
                >
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Join the Travel List"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
